import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { EMPLOYEE_CONFIG } from "../../../../../../constants/employeeConfig";
import { fetchAllReferenceData } from "../../../../../../utils/employee/fetchAllReferenceData";
import { buildTemplateWorksheet } from "../../../../../../utils/employee/buildWorksheet";
import { getDynamicLeaveConfig } from "../../../../../../utils/employee/dynamicLeaveHelper";

// =============================================================================
// EMPLOYEE ID PREPEND FIELD
// Many sheets need EmployeeId as the first column even though employeeId
// belongs to the root section. We define it once here and pass it as
// prependFields to each worksheet builder that needs it.
// =============================================================================
const EMPLOYEE_ID_FIELD = {
  key:         "employeeId",
  label:       "EmployeeId",
  width:       30,
  sampleValue: "<EMP1234>",
  dropdownList: null,
};

// =============================================================================
// GENERATE DOWNLOAD TEMPLATE
// Generates the employee import template Excel file.
// Each sheet corresponds to a section in EMPLOYEE_CONFIG.
//
// Adding a new field:  add it to employeeFields.js — done, no changes here
// Adding a new client-specific field: add feature flag in employeeFields.js — done
// Adding a new sheet: add one buildTemplateWorksheet() call below
// =============================================================================
export const generateDownloadTemplate = async () => {
  const workbook = new ExcelJS.Workbook();

  // Fetch all reference data in one call — replaces all individual fetches
  const refs = await fetchAllReferenceData();

  // -------------------------------------------------------------------------
  // 1. EmployeePersonalInformation
  // Combines root fields and address fields into one sheet
  // -------------------------------------------------------------------------
  await buildTemplateWorksheet(
    workbook,
    "EmployeePersonalInformation",
    {
      // Merge root and address into one combined section for this sheet
      // Both have apiKey: null so they are flat on the employee object
      apiKey:   null,
      feature:  null,
      fields: [
        ...EMPLOYEE_CONFIG.root.fields,
        ...EMPLOYEE_CONFIG.address.fields,
      ],
    },
    refs
  );

  // -------------------------------------------------------------------------
  // 2. EmployeeInformation
  // Prepend EmployeeId so it appears as the first column
  // -------------------------------------------------------------------------
  await buildTemplateWorksheet(
    workbook,
    "EmployeeInformation",
    EMPLOYEE_CONFIG.employeeInformation,
    refs,
    { prependFields: [EMPLOYEE_ID_FIELD] }
  );

  // -------------------------------------------------------------------------
  // 3. AccountInformation
  // -------------------------------------------------------------------------
  await buildTemplateWorksheet(
    workbook,
    "AccountInformation",
    EMPLOYEE_CONFIG.employeeAccountInformation,
    refs,
    { prependFields: [EMPLOYEE_ID_FIELD] }
  );

  // -------------------------------------------------------------------------
  // 4. PassportDetail
  // -------------------------------------------------------------------------
  await buildTemplateWorksheet(
    workbook,
    "PassportDetail",
    EMPLOYEE_CONFIG.employeePassportDetail,
    refs,
    { prependFields: [EMPLOYEE_ID_FIELD] }
  );

  // -------------------------------------------------------------------------
  // 5. SeamanBookDetail
  // buildTemplateWorksheet returns null and skips the sheet automatically
  // when EMPLOYEE_ACCOUNT_SEAMAN_BOOK feature flag is off
  // -------------------------------------------------------------------------
  await buildTemplateWorksheet(
    workbook,
    "SeamanBookDetail",
    EMPLOYEE_CONFIG.employeeSeamanBookDetail,
    refs,
    { prependFields: [EMPLOYEE_ID_FIELD] }
  );

  // -------------------------------------------------------------------------
  // 6. LicenseInformation
  // Skipped automatically when EMPLOYEE_ACCOUNT_LICENSE_INFORMATION is off
  // -------------------------------------------------------------------------
  await buildTemplateWorksheet(
    workbook,
    "LicenseInformation",
    EMPLOYEE_CONFIG.employeeLicenseInformation,
    refs,
    { prependFields: [EMPLOYEE_ID_FIELD] }
  );

  // -------------------------------------------------------------------------
  // 7. BankAccount
  // -------------------------------------------------------------------------
  await buildTemplateWorksheet(
    workbook,
    "BankAccount",
    EMPLOYEE_CONFIG.employeeBankAccounts,
    refs,
    { prependFields: [EMPLOYEE_ID_FIELD] }
  );

  // -------------------------------------------------------------------------
  // 8. EmployeeSalary
  // -------------------------------------------------------------------------
  await buildTemplateWorksheet(
    workbook,
    "EmployeeSalary",
    EMPLOYEE_CONFIG.employeeSalary,
    refs,
    { prependFields: [EMPLOYEE_ID_FIELD] }
  );

  // -------------------------------------------------------------------------
  // 9. Dependents
  // -------------------------------------------------------------------------
  await buildTemplateWorksheet(
    workbook,
    "Dependents",
    EMPLOYEE_CONFIG.employeeDependents,
    refs,
    { prependFields: [EMPLOYEE_ID_FIELD] }
  );

  // -------------------------------------------------------------------------
  // 10. EducationalAttainment
  // -------------------------------------------------------------------------
  await buildTemplateWorksheet(
    workbook,
    "EducationalAttainment",
    EMPLOYEE_CONFIG.employeeEducationAttainments,
    refs,
    { prependFields: [EMPLOYEE_ID_FIELD] }
  );

  // -------------------------------------------------------------------------
  // 11. TrainingAndSeminar
  // -------------------------------------------------------------------------
  await buildTemplateWorksheet(
    workbook,
    "TrainingAndSeminar",
    EMPLOYEE_CONFIG.employeeTrainingSeminars,
    refs,
    { prependFields: [EMPLOYEE_ID_FIELD] }
  );

  // -------------------------------------------------------------------------
  // 12. EmergencyContact
  // -------------------------------------------------------------------------
  await buildTemplateWorksheet(
    workbook,
    "EmergencyContact",
    EMPLOYEE_CONFIG.employeeEmergencyContacts,
    refs,
    { prependFields: [EMPLOYEE_ID_FIELD] }
  );

  // -------------------------------------------------------------------------
  // 13. FamilyData
  // -------------------------------------------------------------------------
  await buildTemplateWorksheet(
    workbook,
    "FamilyData",
    EMPLOYEE_CONFIG.employeeFamilyData,
    refs,
    { prependFields: [EMPLOYEE_ID_FIELD] }
  );

  // -------------------------------------------------------------------------
  // 14. EmployeeLeaveBalances
  // -------------------------------------------------------------------------
  const dynamicLeaveTemplateConfig = await getDynamicLeaveConfig(); // ADDED
  await buildTemplateWorksheet(
    workbook,
    "EmployeeLeaves",
    dynamicLeaveTemplateConfig, // REPLACED: EMPLOYEE_CONFIG.employeeLeave
    refs,
    { prependFields: [EMPLOYEE_ID_FIELD] }
  );

  // -------------------------------------------------------------------------
  // 15. EmployeeStatus
  // -------------------------------------------------------------------------
  await buildTemplateWorksheet(
    workbook,
    "EmployeeStatus",
    EMPLOYEE_CONFIG.employeeStatuses,
    refs,
    { prependFields: [EMPLOYEE_ID_FIELD] }
  );

  // -------------------------------------------------------------------------
  // Finalize and trigger download
  // -------------------------------------------------------------------------
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, "Employee_Template.xlsx");
};